/**
 * `@vello/web-entry` — web-entry country + locale resolution mechanics (ADR-0030, under ADR-0032).
 *
 * How a first-time, anonymous web visitor's MARKET and LANGUAGE are chosen at entry, as two INDEPENDENT,
 * deterministic, total precedence chains (`Country != Locale`, ADR-0032 D3): country never determines locale
 * and locale never determines country. Geo/IP is a country-level HINT, never a lock (ADR-0030 §2) — overridable,
 * never a hard redirect, never served to crawlers. The public URL shape is `/{country}/{locale}/…` with **no**
 * sub-country geographic segment (ADR-0032 D1/D2).
 *
 * PURE + NEUTRAL by construction: nothing here hardcodes a country, currency, or locale literal. The supported
 * country/locale sets and every default are supplied by the caller (the country-pack registry at the app
 * layer), so the platform never assumes one country's language (ADR-0005 §Verification, ADR-0013 neutrality
 * gate). Locale negotiation reuses `@vello/format` (`resolveCountryLocale`), never a re-implementation.
 */
import { resolveCountryLocale } from '@vello/format';
// ---- geoCountry: the edge hint (ADR-0032 D4/D8) ------------------------------------------------------
/**
 * Provider sentinels that are NOT a country — Cloudflare emits `XX` when it cannot geolocate and `T1` for a
 * Tor exit. They are well-formed `[A-Z]{2}` but must never be treated as a market hint.
 */
const GEO_NON_COUNTRY = new Set(['XX', 'T1']);
/**
 * Normalize an edge country header (Cloudflare `cf-ipcountry` / equivalent, produced at trust boundary TB-1)
 * into an ISO-3166-1 alpha-2 country HINT, or `undefined` when absent/unusable. Country-LEVEL only — never a
 * city or region (ADR-0032 D8). The result is only ever a *suggestion*; whether it is a served market is the
 * caller's decision (see {@link resolveEntryCountry}).
 */
export function parseGeoCountry(raw) {
    if (typeof raw !== 'string')
        return undefined;
    const cc = raw.trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(cc))
        return undefined;
    if (GEO_NON_COUNTRY.has(cc))
        return undefined;
    return cc;
}
const normCountry = (c) => typeof c === 'string' && /^[A-Za-z]{2}$/.test(c.trim()) ? c.trim().toUpperCase() : undefined;
/**
 * Resolve which country/market to present at web entry: `explicit → saved → geoCountry → fallback`
 * (ADR-0032 D6). Deterministic and total. Geo is only a hint: a user choice (explicit/saved) is always
 * honored even when unsupported, but an unsupported geo hint falls through to the (supported) fallback.
 */
export function resolveEntryCountry(inputs) {
    const supported = inputs.isSupported ?? (() => true);
    const explicit = normCountry(inputs.explicit);
    if (explicit !== undefined)
        return { country: explicit, source: 'explicit', supported: supported(explicit) };
    const saved = normCountry(inputs.saved);
    if (saved !== undefined)
        return { country: saved, source: 'saved', supported: supported(saved) };
    const geo = normCountry(inputs.geoCountry);
    if (geo !== undefined && supported(geo))
        return { country: geo, source: 'geo', supported: true };
    const fallback = normCountry(inputs.fallback) ?? inputs.fallback.trim().toUpperCase();
    return { country: fallback, source: 'fallback', supported: supported(fallback) };
}
const clean = (s) => typeof s === 'string' && s.trim() !== '' ? s.trim() : undefined;
/**
 * Resolve the display locale at web entry (presentation only — never determines country). Deterministic,
 * total order (ADR-0032 D5 + ADR-0030 §Verification `persisted > URL > Accept-Language > pack default >
 * neutral`), ending in a neutral fallback:
 *
 *   explicit → saved (persisted) → URL segment → account preferredLocale → Accept-Language (negotiated) →
 *   pack default → neutral (`und`)
 *
 * A direct choice (explicit/saved/URL/account) is honored as-is — `Country != Locale` (ADR-0032 D3) means all
 * `country × locale` combinations are valid, so the resolver never overrides a user's language with the
 * country's; `supportedByPack` reports whether the country *recommends* it, for the app to act on.
 */
export function resolveEntryLocale(inputs) {
    const inPack = (loc) => inputs.pack.supportedLocales.some((s) => s.toLowerCase() === loc.toLowerCase());
    const direct = [
        ['explicit', clean(inputs.explicit)],
        ['saved', clean(inputs.saved)],
        ['url', clean(inputs.urlLocale)],
        ['account', clean(inputs.accountLocale)],
    ];
    for (const [source, value] of direct) {
        if (value !== undefined)
            return { locale: value, source, supportedByPack: inPack(value) };
    }
    // Negotiate Accept-Language against the pack, falling back to the pack's recommended default (no baked
    // default anywhere — the fallback is the pack's own, ADR-0005 §Verification / C-13).
    if (clean(inputs.pack.defaultLocale) !== undefined) {
        const r = resolveCountryLocale(inputs.pack, inputs.acceptLanguage);
        return {
            locale: r.locale,
            source: r.matched ? 'accept-language' : 'pack-default',
            supportedByPack: inPack(r.locale),
        };
    }
    return { locale: inputs.neutralFallback ?? 'und', source: 'neutral', supportedByPack: false };
}
const COUNTRY_SEG = /^[a-z]{2}$/;
const LOCALE_SEG = /^[a-z]{2,3}(-[a-z0-9]{2,8})*$/;
/** Normalize a "rest" path to a single leading slash and no trailing slash (except the root `/`). */
function normalizeRest(segments) {
    if (segments.length === 0)
        return '/';
    return `/${segments.join('/')}`;
}
/**
 * Parse a pathname into its optional `/{country}[/{locale}]` prefix + the rest. The first segment counts as a
 * country when it is alpha-2 shaped (or a known country); the second counts as a locale only when the first
 * was a country and it is BCP-47-ish. A country-only prefix (`/zz/help`) resolves to `{country, locale:
 * undefined, rest:'/help'}` so the entry logic can suggest inserting the locale — never a double-country path.
 * **No** sub-country segment is ever recognized as structural: only the country+locale prefix; anything after
 * the locale stays in `rest` (ADR-0032 D1/D2). Shape-based by default; pass predicates to disambiguate against
 * the real registry when a page path could collide with a country/locale segment.
 */
export function parseEntryPath(pathname, known) {
    const segments = (pathname || '/').split('/').filter((s) => s !== '');
    const [first, second, ...restSegs] = segments;
    const firstLc = first?.toLowerCase();
    const secondLc = second?.toLowerCase();
    const countryOk = firstLc !== undefined && COUNTRY_SEG.test(firstLc) && (known?.isCountry?.(firstLc) ?? true);
    if (!countryOk)
        return { rest: normalizeRest(segments) };
    const localeOk = secondLc !== undefined && LOCALE_SEG.test(secondLc) && (known?.isLocale?.(secondLc) ?? true);
    if (localeOk)
        return { country: firstLc, locale: secondLc, rest: normalizeRest(restSegs) };
    // Country present, no locale: everything after the country segment is the rest (the locale gets inserted).
    return { country: firstLc, locale: undefined, rest: normalizeRest(second === undefined ? [] : [second, ...restSegs]) };
}
/** Build a canonical `/{country}/{locale}/…` path. `rest` (default `/`) is appended after the locale. */
export function buildEntryPath(country, locale, rest = '/') {
    const tail = rest.replace(/^\/+/, '');
    const base = `/${country.toLowerCase()}/${locale.toLowerCase()}`;
    return tail === '' ? base : `${base}/${tail}`;
}
/**
 * The `hreflang` alternates for a country and its supported locales — one per served locale, plus an optional
 * `x-default` (the pack's recommended default) so the geo default stays SEO-safe. Segments are data-driven
 * from the pack's `supportedLocales`, never hardcoded (ADR-0030 §4).
 */
export function hreflangAlternates(country, pack, rest = '/') {
    const alternates = pack.supportedLocales.map((locale) => ({
        hreflang: locale,
        href: buildEntryPath(country, locale, rest),
    }));
    const def = clean(pack.defaultLocale);
    if (def !== undefined) {
        alternates.push({ hreflang: 'x-default', href: buildEntryPath(country, def, rest) });
    }
    return alternates;
}
/** The canonical self-URL for a localized page — the addressed `/{country}/{locale}/…` path itself. */
export function canonicalPath(country, locale, rest = '/') {
    return buildEntryPath(country, locale, rest);
}
// ---- Hint-not-lock: suggest, never lock (ADR-0030 §2) -------------------------------------------------
/** Substrings that mark a request as an automated crawler/bot — matched case-insensitively in the UA. */
const CRAWLER_UA = [
    'bot',
    'crawl',
    'spider',
    'slurp',
    'mediapartners',
    'facebookexternalhit',
    'embedly',
    'quora link preview',
    'whatsapp',
    'telegrambot',
];
/** Whether a user-agent looks like a crawler/bot — such requests must never receive a geo redirect. */
export function isCrawler(userAgent) {
    if (typeof userAgent !== 'string' || userAgent.trim() === '')
        return false;
    const ua = userAgent.toLowerCase();
    return CRAWLER_UA.some((needle) => ua.includes(needle));
}
/**
 * Decide whether to SUGGEST a soft redirect to the localized entry `/{country}/{locale}/…` (ADR-0030 §2:
 * suggest, don't lock). Never for a crawler (SEO-safe — the crawler indexes the canonical/hreflang set), and
 * never when the path is already localized (a deep/shared link renders itself, unrewritten). Otherwise it
 * returns the target for the app to navigate to softly, keeping a one-click override.
 */
export function suggestEntryRedirect(inputs) {
    if (isCrawler(inputs.userAgent))
        return { redirect: false, reason: 'crawler' };
    const parsed = parseEntryPath(inputs.pathname, inputs.known);
    if (parsed.country !== undefined && parsed.locale !== undefined) {
        return { redirect: false, reason: 'already-localized' };
    }
    return { redirect: true, to: buildEntryPath(inputs.country, inputs.locale, parsed.rest), reason: 'suggest' };
}
//# sourceMappingURL=web-entry.js.map